import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { CreateRuleArticleInput, UpdateRuleArticleInput } from '@proref/schemas';
import {
  createRuleArticle,
  updateRuleArticle,
  deleteRuleArticle,
  bulkCreateRuleArticles,
  extractRulesFromPdf,
} from '../services/rules.service';

// Rule-article authoring mutations. Each write invalidates the sport's rule-book (['rules', slug])
// so the list reflects the change immediately. The read hook `useRulesQuery` lives in use-sports.

export function useCreateRuleArticle(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateRuleArticleInput) => createRuleArticle(slug, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['rules', slug] }),
  });
}

export function useUpdateRuleArticle(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateRuleArticleInput }) =>
      updateRuleArticle(id, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['rules', slug] }),
  });
}

export function useDeleteRuleArticle(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteRuleArticle(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['rules', slug] }),
  });
}

export function useBulkCreateRuleArticles(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (articles: CreateRuleArticleInput[]) => bulkCreateRuleArticles(slug, articles),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['rules', slug] }),
  });
}

// Extraction doesn't persist anything, so there's nothing to invalidate — the drafts flow into
// the wizard's review step and only bulk-create writes.
export function useExtractRules(slug: string) {
  return useMutation({
    mutationFn: (pdfBase64: string) => extractRulesFromPdf(slug, pdfBase64),
  });
}
